import { LazyAction, LazyImport, LazyModule } from 'owa-bundling';
import { runAfterInitialRender } from 'owa-delayed-initialization';
import EventEmitterClass from 'owa-event-emitter';
import {
    // Notification consumers
    subscribe,
    unsubscribe,
    getChannelId,
    // Interfaces & Enums
    NotificationSubscription,
    NotificationCallback,
    NotificationManagerConfig,
    SubscriptionState,
    SubscriptionStatus,
} from 'owa-notification-manager';
import NotificationEventType from './schema/NotificationEventType';

export interface OwaNotification {
    subscribe: typeof subscribe;
    unsubscribe: typeof unsubscribe;
    getChannelId: typeof getChannelId;
}

/* tslint:disable:no-namespace */
declare global {
    interface Window {
        owaNotification: OwaNotification;
    }
}
/* tslint:enable:no-namespace */

runAfterInitialRender(async () => {
    // Expose notification consumer function refs for components webpacked outside of owa-mail
    let subscribeRef = await lazySubscribe.import();
    let unsubscribeRef = await lazyUnsubscribe.import();
    let getChannelIdRef = await lazyGetChannelId.import();

    // If the dependent component loads before owa-notification,
    // it should check to see if window.owNotification is populated first.
    window.owaNotification = {
        subscribe: subscribeRef,
        unsubscribe: unsubscribeRef,
        getChannelId: getChannelIdRef,
    };

    // If it isn't yet populated, it should register a listener to the 'owaNotificationLoaded' event.
    // After that event fires, window.owaNotification will definitely be populated.
    window.dispatchEvent(new Event('owaNotificationLoaded'));
});

const lazyModule = new LazyModule(() =>
    import(/* webpackChunkName: "Notification", webpackPrefetch: true */ 'owa-notification-manager')
);

// Lazy functions for notification consumers
export const lazySubscribe = new LazyAction(lazyModule, m => m.subscribe);
export const lazyUnsubscribe = new LazyAction(lazyModule, m => m.unsubscribe);
export const lazyGetChannelId = new LazyImport(lazyModule, m => m.getChannelId);
export const lazyGetChannelReady = new LazyImport(lazyModule, m => m.getChannelReady);

// Lazy functions for channel initialization
export const lazyInitializeNotificationManager = new LazyAction(
    lazyModule,
    m => m.initializeNotificationManager
);

export const lazyGetNotificationEmitter = new LazyImport(lazyModule, m => m.getNotificationEmitter);

// Interfaces & Enums
export {
    NotificationSubscription,
    NotificationCallback,
    NotificationManagerConfig,
    SubscriptionState,
    SubscriptionStatus,
    NotificationEventType,
};

let EventEmitter = typeof EventEmitterClass;
export { EventEmitter };
